import React from "react";
import { Check, X } from "lucide-react";

interface PasswordRequirementsProps {
  password: string;
  passwordStrength: number;
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  passwordStrength,
}) => {
  const requirements = [
    {
      text: "At least 8 characters",
      met: password.length >= 8,
    },
    {
      text: "One uppercase letter",
      met: /[A-Z]/.test(password),
    },
    {
      text: "One lowercase letter",
      met: /[a-z]/.test(password),
    },
    {
      text: "One number",
      met: /[0-9]/.test(password),
    },
    {
      text: "One special character (optional)",
      met: /[^A-Za-z0-9]/.test(password),
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-lg">
      <h4 className="text-sm font-medium text-gray-700 mb-3">
        Password Requirements
      </h4>
      <div className="space-y-2">
        {requirements.map((requirement, index) => (
          <div key={index} className="flex items-center space-x-2">
            <div
              className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                requirement.met
                  ? "bg-green-100 text-green-600"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              {requirement.met ? (
                <Check size={10} />
              ) : (
                <X size={10} />
              )}
            </div>
            <span
              className={`text-xs ${
                requirement.met ? "text-green-600" : "text-gray-500"
              }`}
            >
              {requirement.text}
            </span>
          </div>
        ))}
      </div>
      
      {passwordStrength > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">Strength:</span>
            <span
              className={`text-xs font-medium ${
                passwordStrength < 25
                  ? "text-red-500"
                  : passwordStrength < 50
                  ? "text-orange-500"
                  : passwordStrength < 70
                  ? "text-yellow-500"
                  : passwordStrength < 90
                  ? "text-green-500"
                  : "text-emerald-500"
              }`}
            >
              {passwordStrength < 25
                ? "Weak"
                : passwordStrength < 50
                ? "Fair"
                : passwordStrength < 70
                ? "Good"
                : passwordStrength < 90
                ? "Strong"
                : "Very Strong"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordRequirements;
